import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './index.scss';

const Select = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = "请选择...",
  className = "",
  disabled = false,
  size = "normal", // small, normal, large
  dropdownPortal = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  // 获取选中项的显示文本
  const getDisplayValue = () => {
    const selectedOption = options.find(option => option.value === value);
    return selectedOption ? selectedOption.label : '';
  };

  // 计算下拉框位置
  const calculateDropdownPosition = () => {
    if (!selectRef.current) return;
    
    const rect = selectRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    setDropdownPosition({
      top: rect.bottom + scrollTop,
      left: rect.left + scrollLeft,
      width: rect.width
    });
  };

  // 处理选项点击
  const handleOptionClick = (option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  // 处理点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      calculateDropdownPosition();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 处理键盘事件
  const handleKeyDown = (event) => {
    if (disabled) return;
    
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        setIsOpen(!isOpen);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          // 导航到下一个选项的逻辑可以在这里添加
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        // 导航到上一个选项的逻辑可以在这里添加
        break;
    }
  };

  // 下拉框内容
  const dropdownContent = (
    <div 
      ref={dropdownRef}
      className={`c-select-dropdown ${isOpen ? 'open' : ''}`}
      style={dropdownPortal ? {
        position: 'absolute',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 10001
      } : {}}
    >
      {options.map((option) => (
        <div
          key={option.value}
          className={`c-select-option ${option.value === value ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
          onClick={() => !option.disabled && handleOptionClick(option)}
        >
          {option.label}
        </div>
      ))}
      {options.length === 0 && (
        <div className="c-select-option disabled">暂无选项</div>
      )}
    </div>
  );

  return (
    <div className={`c-select ${className} ${size} ${disabled ? 'disabled' : ''}`} ref={selectRef}>
      <div
        className={`c-select-header ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="c-select-value">
          {getDisplayValue() || placeholder}
        </span>
        <span className="c-select-arrow">
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>
      
      {isOpen && (
        dropdownPortal ? 
        createPortal(dropdownContent, document.body) : 
        dropdownContent
      )}
    </div>
  );
};

export default Select;